import { useCallback, useRef } from "react";
import { Dispatch } from "react";
import { IQrCodeAction, QrCodeActions, usePlApiContextFetch } from "@swiggy-private/portal-dineout-context";

import { getRestaurantConfig } from "../api";
import { CURTAIN_RAISER_FREQUENCY_FLAG_TIMEOFFSET_MS } from "../components/CurtainRaiser/constants";
import { SUCCESS_STATUS_CODE, STORAGE_KEYS } from "../constants";
import { isFrequencyCappedWithTableId } from "../utils/frequencyCapping";

interface IUseCurtainRaiserPrefetchParams {
    restaurantId: string;
    tableId: string;
    dispatch: Dispatch<IQrCodeAction>;
}

export const useCurtainRaiserPrefetch = ({ restaurantId, tableId, dispatch }: IUseCurtainRaiserPrefetchParams) => {
    const makePlApiCall = usePlApiContextFetch();

    // Consent last fetched with — guards against duplicate calls on effect re-runs / dev double-invoke.
    const curtainFetchedConsentRef = useRef<boolean | null>(null);
    // Holds the most recently scheduled prefetchCurtainRaiser call. Each new call attaches
    // itself after this one, so calls run one at a time instead of overlapping. By the time a
    // call gets its turn, it checks curtainFetchedConsentRef — if an earlier call already
    // fetched this consent, it's a no-op instead of a duplicate fetch.
    const curtainFetchChainRef = useRef<Promise<boolean>>(Promise.resolve(true));
    const hasFetchedCurtainRef = useRef(false);

    // Frequency-cap check (skipped on a forced refetch, e.g. after the age gate) then the fetch.
    const runCurtainFetch = useCallback(
        async (consent: boolean, force: boolean): Promise<boolean> => {
            if (!makePlApiCall) return false;

            if (!force) {
                const isFrequencyCapped = await isFrequencyCappedWithTableId(
                    STORAGE_KEYS.CURTAIN_RAISER,
                    tableId,
                    CURTAIN_RAISER_FREQUENCY_FLAG_TIMEOFFSET_MS,
                );
                // Capped counts as success (nothing to fetch), but don't record the consent
                // value — a later forced refetch with the same value must still be allowed.
                if (isFrequencyCapped) return true;
            }

            dispatch({ type: QrCodeActions.CURTAIN_SCREEN_LOADING, payload: {} });
            try {
                const response = await getRestaurantConfig(
                    { searchParams: { tableId, restaurantId, consentLDA: String(consent) } },
                    makePlApiCall,
                );
                if (response?.statusCode === SUCCESS_STATUS_CODE) {
                    curtainFetchedConsentRef.current = consent;
                    dispatch({ type: QrCodeActions.CURTAIN_SCREEN_SUCCESS, payload: { data: response } });
                    return true;
                }
                dispatch({ type: QrCodeActions.CURTAIN_SCREEN_ERROR, payload: {} });
                return false;
            } catch {
                dispatch({ type: QrCodeActions.CURTAIN_SCREEN_ERROR, payload: {} });
                return false;
            }
        },
        [makePlApiCall, restaurantId, tableId, dispatch],
    );

    const prefetchCurtainRaiser = useCallback(
        (consent = true, force = false): Promise<boolean> => {
            const fetchIfNeeded = (): Promise<boolean> => {
                if (!makePlApiCall || !restaurantId || !tableId) return Promise.resolve(false);
                // An earlier call already fetched this consent by the time our turn came up.
                if (curtainFetchedConsentRef.current === consent) return Promise.resolve(true);
                return runCurtainFetch(consent, force);
            };

            // Wait for whatever call is currently running to finish — succeeded or failed,
            // we don't care which, we just don't want to overlap with it — then run this call.
            const waitForPriorCall = curtainFetchChainRef.current.catch(() => undefined);
            const thisCall = waitForPriorCall.then(fetchIfNeeded);
            curtainFetchChainRef.current = thisCall;
            return thisCall;
        },
        [makePlApiCall, restaurantId, tableId, runCurtainFetch],
    );

    return { prefetchCurtainRaiser, hasFetchedCurtainRef };
};
